import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Catches render errors so one bad component does not blank the whole page.
 *
 * Without this, a single thrown error in any component unmounts the entire
 * React tree and the user gets a white screen with no explanation and no way
 * back — the worst possible failure, because it looks like the site is gone
 * rather than one thing being broken.
 */
interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Phase 1 has no error reporting service wired (backlog T4). Console is
    // the only sink for now, but at least the failure is visible.
    console.error('[render error]', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="grid min-h-dvh place-items-center px-4">
        <div className="surface-raised w-full max-w-md rounded-2xl px-6 py-10 text-center">
          <div
            className="mx-auto grid size-12 place-items-center rounded-full"
            style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}
          >
            <svg viewBox="0 0 24 24" className="size-6" fill="none" aria-hidden>
              <path
                d="M12 8v5M12 17h.01M10.3 3.9L2.4 17.3A2 2 0 004.1 20.3h15.8a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h1 className="mt-5 text-2xl font-semibold">Something broke on this page</h1>
          <p className="mt-2 text-sm text-[var(--ink-2)]">
            Not your fault. Reloading usually fixes it.
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-xl px-4 py-2.5 text-sm font-medium text-white"
              style={{ background: 'var(--marigold)' }}
            >
              Reload
            </button>
            <a href="/" className="surface rounded-xl px-4 py-2.5 text-sm font-medium">
              Back to home
            </a>
          </div>

          {import.meta.env.DEV && (
            <pre
              className="mt-6 overflow-x-auto rounded-lg p-3 text-left text-xs text-[var(--danger)]"
              style={{ background: 'var(--surface-2)' }}
            >
              {error.message}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
